import { useEffect, useState } from "react";
import { Output } from "valibot";

import { getChatHistory } from "shared/api";
import { generateUuid } from "shared/functions";

import { ChatDataSchema, ChatHistory, LiveAgentHandoffInfo } from "./schemas";
import { getRestoredChatData } from "./functions";

function useChatHistory(
  token: string,
  botId: string,
  configChatId: string | undefined,
  eventSource: EventSource | null,
  setChatData: React.Dispatch<
    React.SetStateAction<Output<typeof ChatDataSchema>>
  >,
  instanceName: string,
  setUseWebsocketGeneration: React.Dispatch<React.SetStateAction<boolean>>,
  setLiveAgentHandoffInfo: React.Dispatch<
    React.SetStateAction<Output<typeof LiveAgentHandoffInfo> | null>
  >,
  setHasChatEnded: React.Dispatch<React.SetStateAction<boolean>>,
) {
  const [chatId, setChatId] = useState<string>("");
  const [chatHistory, setChatHistory] = useState<Output<
    typeof ChatHistory
  > | null>(null);
  const [isChatReady, setIsChatReady] = useState<boolean>(false);

  useEffect(() => {
    const fetchAndSetChatHistory = async () => {
      // get chat id from session storage if it exists else generate a new one
      const restoredChatData = getRestoredChatData(instanceName, token);
      const finalChatId =
        configChatId || restoredChatData?.chatId || generateUuid();
      setChatId(finalChatId);

      try {
        const chatHistory = await getChatHistory({
          token,
          chatId: finalChatId,
        });
        setChatHistory(chatHistory);
        setChatData(chatHistory?.chat_data || []);
        setLiveAgentHandoffInfo(chatHistory?.live_agent_handoff_info);
        setHasChatEnded(chatHistory?.chat_ended || false);
        // in case of live agent handoff, use websocket
        const shouldUseWebsocket =
          !!chatHistory?.live_agent_handoff_info?.is_active;
        setUseWebsocketGeneration(shouldUseWebsocket);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error fetching chat history", err);
      } finally {
        setIsChatReady(true);
      }
    };

    fetchAndSetChatHistory();
  }, [
    configChatId,
    instanceName,
    setChatData,
    setUseWebsocketGeneration,
    token,
    setLiveAgentHandoffInfo,
    setHasChatEnded,
  ]);

  const resetChat = () => {
    setChatData([]);
    setChatId(generateUuid());
    setHasChatEnded(false);
    eventSource?.close?.();
    setUseWebsocketGeneration(false);
  };

  return {
    chatId,
    setChatId,
    resetChat,
    chatHistory,
    isChatReady,
  };
}

export default useChatHistory;
